import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import CryptoJS from 'crypto-js';
import Cookie from 'js-cookie';
import {
  HEADER_DARK_MODE_PAGE_LIST,
  HIDE_DT_BANNER_PAGE_LIST,
  HIDE_FOOTER_PAGE_LIST,
  HIDE_HEADER_PAGE_LIST,
  MOBILE_SCREEN_WIDTH,
  SHOW_APP_DOWNLOAD_BANNER_PAGE_LIST,
  SHOW_FOOTER_SUBSCRIBE_PAGE_LIST,
} from './constants';
import { EPlatformKey } from '@/utils/enums/platform-enum';

/**
 * 根据UserAgent判断浏览器环境类型
 * @param ua 用户代理字符串
 * @returns WEB 表示桌面环境，WAP 表示移动环境。默认使用 WAP
 */
export function getBrowserEnvironment(ua = ''): 'WEB' | 'WAP' {
  if (ua.trim() === '') {
    return 'WAP';
  }
  // 使用移动设备特征正则表达式检测
  const isMobile = /(Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone|Mobile)/i.test(ua);
  // 排除iPad桌面模式检测
  const isMacintosh = /Macintosh/i.test(ua);
  if (isMacintosh || !isMobile) {
    // 仅明确的桌面端场景返回 WEB
    return 'WEB';
  } else {
    // 移动特征匹配到 / UserAgent为空 / 其他未识别场景 → 都返回 WAP
    return 'WAP';
  }
}

export function getPlatformKey(ua = ''): EPlatformKey {
  const inApp = ua.includes('nextmar-xms-app');
  const isAndroid = /android/i.test(ua);
  if (inApp) {
    return isAndroid ? 'ANDROID' : 'IOS';
  }
  const browserEnv = getBrowserEnvironment(ua);
  return browserEnv;
}

// 判断是否是移动端屏幕
export function isMobileScreenFunc() {
  return window.innerWidth < MOBILE_SCREEN_WIDTH;
}
// 判断是否在APP中
export function isInZeloolApp() {
  if (typeof window === 'undefined') {
    return false;
  }
  const isInApp = navigator.userAgent.includes('nextmar-xms-app');
  return isInApp;
}

export class CryptoUtils {
  static KEY = CryptoJS.enc.Utf8.parse('zlmasternxcac0rb');
  static IV = CryptoJS.enc.Utf8.parse('zlmasterc447exzu');

  encrypt(message) {
    try {
      const encrypted = CryptoJS.AES.encrypt(message, CryptoUtils.KEY, {
        iv: CryptoUtils.IV,
        padding: CryptoJS.pad.Pkcs7,
        mode: CryptoJS.mode.CBC,
      });
      // 将加密后的数据转换为 Base64 编码的字符串
      const encryptedBase64 = encrypted.ciphertext.toString(CryptoJS.enc.Base64);
      return encodeURIComponent(encryptedBase64);
    } catch (error) {
      logger.log(error);
      return '';
    }
  }

  decrypt(encryptedBase64UrlSafe) {
    try {
      // 将 URL 安全的 Base64 编码转换回标准 Base64 编码
      const encryptedBase64 = decodeURIComponent(encryptedBase64UrlSafe);
      const encryptedWordArray = CryptoJS.enc.Base64.parse(encryptedBase64);
      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: encryptedWordArray } as any,
        CryptoUtils.KEY,
        {
          iv: CryptoUtils.IV,
          padding: CryptoJS.pad.Pkcs7,
          mode: CryptoJS.mode.CBC,
        },
      );
      const decryptedMessage = decrypted.toString(CryptoJS.enc.Utf8);
      return decryptedMessage;
    } catch (error) {
      logger.log(error);
      return '';
    }
  }
}

// 分享（实际功能有效性待验证）
export function SimpleShare(options: Record<string, string> = {}) {
  // get share content
  let url = options.url || window.location.href;
  let title = options.title || document.title;
  let content = options.content || '';
  let pic = options.pic || '';
  const hashtag = options.hashtag || '';
  // fix content format
  url = encodeURIComponent(url);
  title = encodeURIComponent(title);
  content = encodeURIComponent(content);
  pic = encodeURIComponent(pic);
  // share target url
  const facebook =
    'https://www.facebook.com/sharer/sharer.php?u={url}&quote={title}&hashtag={hashtag}'; // 实测hashtag无效
  const twitter = 'https://twitter.com/intent/tweet?text={title}&url={url}';
  const googleplus = 'https://plus.google.com/share?title={title}&url={url}';
  const pinterest = 'https://www.pinterest.com/pin/create/button/?url={url}';
  const messenger = `https://www.facebook.com/dialog/send?app_id=${env.NEXT_PUBLIC_FACEBOOK_MESSAGE_APP_ID}&link={url}&redirect_uri={url}`;
  const whatsApp = `https://wa.me/?text={url}`;
  // replace content functions
  function replaceAPI(api) {
    return api
      .replaceAll('{url}', url)
      .replace('{title}', title)
      .replace('{content}', content)
      .replace('{pic}', pic)
      .replace('{hashtag}', hashtag);
  }
  function popopen(type, val?) {
    if (val && val === 'facebook') {
      // 否则，打开新的窗口
      window.open(
        replaceAPI(type),
        `${Date.now()}`,
        'toolbar=0,resizable=1,status=0,width=640,height=528,left=500,top=300',
      );
    } else {
      window.open(
        replaceAPI(type),
        't',
        'toolbar=0,resizable=1,status=0,width=640,height=528,left=500,top=300',
      );
    }
  }
  // share target
  this.googleplus = function () {
    popopen(googleplus);
  };
  this.facebook = function () {
    popopen(facebook, 'facebook');
  };
  this.twitter = function () {
    popopen(twitter);
  };
  this.pinterest = function () {
    popopen(pinterest);
  };
  this.messenger = function () {
    popopen(messenger);
  };
  this.whatsApp = function () {
    popopen(whatsApp);
  };
}

/**
 * @description: 将16进制的颜色，转换为Rgba模式
 * @param {*} str 16进制颜色
 * @param {*} opacity 透明度
 * @return {*}
 */
export function set16ToRgba(str, opacity) {
  const reg = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
  if (!reg.test(str)) {
    return;
  }
  let newStr = str.toLowerCase().replace(/#/g, '');
  const len = newStr.length;
  if (len === 3) {
    let t = '';
    for (let i = 0; i < len; i++) {
      t += newStr.slice(i, i + 1).concat(newStr.slice(i, i + 1));
    }
    newStr = t;
  }
  const arr = []; // 将字符串分隔，两个两个的分隔
  for (let i = 0; i < 6; i += 2) {
    const s = newStr.slice(i, i + 2);
    arr.push(parseInt(s, 16));
  }
  return `rgba(${arr.join(',')},${opacity})`;
}

/**
 * @description: 数据非空验证,验证数据是否为 null undefined [] {} ''
 * @param {any} data 需要进行数据的非空验证的数据
 * @return {boolean} 为空返回true,否则返回false
 */
export const isEmpty = (data: any) => {
  if (data === null) return true;
  if (data === undefined) return true;
  if (Object.prototype.toString.call(data) === '[object Array]') {
    return data.length === 0;
  }
  if (Object.prototype.toString.call(data) === '[object Object]') {
    return Object.keys(data).length === 0;
  }
  if (typeof data === 'string') {
    return data.trim() === '';
  }
  return false;
};

// 将电话号码隐私化处理
export function privacyPhone(phone: string, telCode: string) {
  const areaNumber = telCode ? `+${telCode} ` : '';
  let phoneNumber = phone ? phone.replace(/\((.*?)\)/g, '$1').replace('-', '') : '';
  if (phoneNumber.length > 7) {
    const start = phoneNumber.slice(0, 3);
    const end = phoneNumber.slice(phoneNumber.length - 4, phoneNumber.length);
    let star = '';
    for (let i = 0; i < phoneNumber.length - 7; i++) {
      star += '*';
    }
    phoneNumber = start + star + end;
  }
  return areaNumber + phoneNumber;
}

export const relationEmailSuffix = [
  '@gmail.com',
  '@yahoo.com',
  '@hotmail.com',
  '@aol.com',
  '@icloud.com',
  '@outlook.com',
  '@msn.com',
  '@comcast.net',
  '@live.com',
];

export const relationEmailMaxLength = relationEmailSuffix.reduce(function (longest, currentWord) {
  return Math.max(longest, currentWord.length);
}, 0);

/**
 * 解析复合电话号码字符串
 * @param value 复合电话号码，如 "US(+1)1234567890"
 * @returns 拆分后的国家代码和电话号码
 */
export const parsePhoneValue = (value?: string): { telCode: string; telephone: string } => {
  if (!value) return { telCode: env.NEXT_PUBLIC_LOCALE_DEFAULT_TELCODE, telephone: '' };

  // 尝试匹配国家代码部分
  const countryCodeMatch = value.match(/^([A-Z]{2}\(\+\d+(?:-\d+)?\))/);

  if (!countryCodeMatch) {
    // 未匹配到国家代码
    return { telCode: '', telephone: value };
  }

  const telCode = countryCodeMatch[1];
  const telephone = value.substring(telCode.length);

  return { telCode, telephone };
};

/**
 * 深度克隆一个值
 * @template T 要克隆的值的类型
 * @param obj 要克隆的值
 * @param hash 用于处理循环引用的 WeakMap
 * @returns 克隆后的值
 */
export function deepClone<T>(obj: T, hash = new WeakMap<object, unknown>()): T {
  // 处理基本类型和 null
  if (obj === null) return obj;

  // 处理特殊对象类型
  if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
  if (obj instanceof RegExp) return new RegExp(obj) as unknown as T;
  if (obj instanceof Map) {
    return new Map(Array.from(obj, ([key, val]) => [key, deepClone(val, hash)])) as unknown as T;
  }

  if (obj instanceof Set) {
    return new Set(Array.from(obj, (val) => deepClone(val, hash))) as unknown as T;
  }

  // 如果不是对象，直接返回
  if (typeof obj !== 'object') return obj;

  // 处理循环引用
  if (hash.has(obj as object)) return hash.get(obj as object) as T;

  // 处理数组
  if (Array.isArray(obj)) {
    const cloneArr = obj.map((item) => deepClone(item, hash));
    hash.set(obj as object, cloneArr);
    return cloneArr as unknown as T;
  }

  // 处理普通对象
  const cloneObj = Object.create(Object.getPrototypeOf(obj));
  hash.set(obj as object, cloneObj);

  Object.entries(obj as object).forEach(([key, value]) => {
    cloneObj[key] = deepClone(value, hash);
  });

  return cloneObj as T;
}

/**
 * 过滤字符中特殊字符，用于地址中输入的姓名
 * @param {*} str
 * @returns
 */
export function hasSpecialChar(str: string) {
  const specialChar = "·……*-，。.；：'/\\|?><}{][()&^%$#!~`".split('');
  const filteredStr = str
    .split('')
    .filter((char) => !specialChar.includes(char))
    .join('');
  return filteredStr;
}

export function generateRegexBasedKey(length = 12): string {
  const regex = /^[a-zA-Z0-9]$/;
  const validChars: string[] = [];
  // 生成ASCII 32-126可打印字符（含空格）
  for (let i = 32; i <= 126; i++) {
    const char = String.fromCharCode(i);
    if (regex.test(char)) validChars.push(char);
  }

  return Array.from(
    { length },
    () => validChars[Math.floor(Math.random() * validChars.length)],
  ).join('');
}

/**
 * 生成全局唯一的方法名，用户 h5 和 app 交互用的回调
 * @param {*} defaultVal
 * @returns
 */
export function randomFuncName(defaultVal) {
  // 判断老版本号，小于2.9.6的版本，使用默认值，否则里生成新的唯一回调方法名
  const version = getQueryByName('version') || '';
  const moreThan = versionCompare(version, '2.9.6', '>=');
  if (moreThan || !version) {
    return `app${randomString()}`;
  } else {
    return defaultVal;
  }
}
// 查找路由的search
export function getQueryByName(name) {
  const url = location.search;
  const reg = new RegExp(`[?&]${name}=([^&#]+)`);
  const query = url.match(reg);
  return query ? query[1] : '';
}
// 用于版本大小的比较
export function versionCompare(v1, v2, operator) {
  const version1 = v1.replaceAll('-', '.');
  const version2 = v2.replaceAll('-', '.');
  // 输入验证
  if (
    typeof version1 !== 'string' ||
    typeof version2 !== 'string' ||
    !['<', '<=', '=', '==', '>', '>='].includes(operator)
  ) {
    return false;
  }

  const parsePart = (part) => {
    if (/^[a-z]+$/.test(part)) {
      return part.toLowerCase();
    }
    const num = parseInt(part, 10);
    if (isNaN(num)) {
      return -1;
    }
    return num;
  };

  const v1Arr = version1.split('.').map(parsePart);
  const v2Arr = version2.split('.').map(parsePart);

  const maxLength = Math.max(v1Arr.length, v2Arr.length);

  for (let i = 0; i < maxLength; i++) {
    const v1Part = v1Arr[i] || 0;
    const v2Part = v2Arr[i] || 0;

    if (v1Part < v2Part) {
      return ['<', '<='].includes(operator);
    } else if (v1Part > v2Part) {
      return ['>', '>='].includes(operator);
    }
  }

  return ['=', '=='].includes(operator);
}

/* 生成 32 位随机的字符串，目前用于dmp生成页面uuid */
export function randomString(num?: number) {
  let ret = '';
  if (typeof crypto.randomUUID === 'function') {
    ret = crypto.randomUUID().replaceAll('-', '');
  } else {
    const e = num || 32;
    const t = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678';
    const a = t.length;
    for (let i = 0; i < e; i++) {
      ret += t.charAt(Math.floor(Math.random() * a));
    }
    for (let i = 0; i < e; i++) ret += t.charAt(Math.floor(Math.random() * a));
  }
  return ret;
}

export function getTagPath(name) {
  if (!name) return '';
  return name.replace(/\s+/g, '-').toLowerCase();
}

// 加法
export const add = (arg1: number | string, arg2: number | string) => {
  let r1;
  let r2;
  const num1 = Number(arg1);
  const num2 = Number(arg2);
  try {
    r1 = num1.toString().split('.')[1].length;
  } catch {
    r1 = 0;
  }
  try {
    r2 = num2.toString().split('.')[1].length;
  } catch {
    r2 = 0;
  }
  /* 这里在原有的小数位上+1，可以避免一些问题。比如 add(268.34, 0.83) 会出问题。原因在于 268.33*100 会出现不精确的结果 */
  const m = Math.pow(10, Math.max(r1 + 1, r2 + 2));
  const n = r1 >= r2 ? r1 : r2;
  const ret = ((num1 * m + num2 * m) / m).toFixed(n);
  return parseFloat(ret);
};

// 减法
export const accSub = (arg1: number | string, arg2: number | string) => {
  let r1;
  let r2;
  const num1 = Number(arg1);
  const num2 = Number(arg2);
  try {
    r1 = num1.toString().split('.')[1].length;
  } catch {
    r1 = 0;
  }
  try {
    r2 = num2.toString().split('.')[1].length;
  } catch {
    r2 = 0;
  }
  const m = Math.pow(10, Math.max(r1 + 1, r2 + 2));
  const n = r1 >= r2 ? r1 : r2;
  return ((num1 * m - num2 * m) / m).toFixed(n);
};

// 乘法
export const accMul = (arg1: number | string, arg2: number | string) => {
  let m = 0;
  const s1 = arg1.toString();
  const s2 = arg2.toString();
  try {
    const sub = s1.split('.')[1];
    if (sub && sub.length) {
      m += sub.length;
    }
  } catch (e) {
    logger.log(e);
  }
  try {
    const sub = s2.split('.')[1];
    if (sub && sub.length) {
      m += sub.length;
    }
  } catch (e) {
    logger.log(e);
  }
  return (Number(s1.replace('.', '')) * Number(s2.replace('.', ''))) / Math.pow(10, m);
};

// 除法
export const accDiv = (arg1: number | string, arg2: number | string) => {
  let t1 = 0;
  let t2 = 0;
  // 分母不存在或者为0，则返回arg1
  if (!arg2 || arg2 === '0') {
    return arg1;
  }
  try {
    const sub = arg1.toString().split('.')[1];
    if (sub && sub.length) {
      t1 = sub.length;
    }
  } catch (e) {
    logger.log(e);
  }
  try {
    const sub = arg2.toString().split('.')[1];
    if (sub && sub.length) {
      t2 = sub.length;
    }
  } catch (e) {
    logger.log(e);
  }
  return (
    (Number(arg1.toString().replace('.', '')) / Number(arg2.toString().replace('.', ''))) *
    Math.pow(10, t2 - t1)
  );
};

// 获取cookie的工具函数
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}
/* 格式化数字，使用国际化数字格式
 * @param {number|string} num 需要格式化的数字
 * @param {number} precision 小数位数，默认为2
 * @param {string} locales 地区设置，默认为'en-US'（德国格式，使用点作为千位分隔符，逗号作为小数分隔符）
 * @param {boolean} isCompulsoryDecimal 是否保留小数，默认为true
 * @returns {string|number} 格式化后的数字字符串，如果输入无效则返回原值
 * // 语言 + 地区
'en-US'   // 美式英语
'en-GB'   // 英式英语
'en-CA'   // 加拿大英语
'en-AU'   // 澳大利亚英语

'zh-CN'   // 中国大陆中文
'zh-TW'   // 台湾中文
'zh-HK'   // 香港中文

'pt-BR'   // 巴西葡萄牙语
'pt-PT'   // 葡萄牙葡萄牙语

'fr-FR'   // 法国法语
'fr-CA'   // 加拿大法语
'fr-BE'   // 比利时法语

'de-DE'   // 德国德语
'de-AT'   // 奥地利德语
'de-CH'   // 瑞士德语

// 语言 + 脚本
'zh-Hans'  // 简体中文
'zh-Hant'  // 繁体中文

// 语言 + 脚本 + 地区
'zh-Hans-CN'  // 中国大陆使用的简体中文
'zh-Hans-SG'  // 新加坡使用的简体中文
'zh-Hant-TW'  // 台湾使用的繁体中文
'zh-Hant-HK'  // 香港使用的繁体中文
 */
export const formatNumber = (
  num: number | string | null | undefined,
  precision = 2,
  locales = 'en-US',
  isCompulsoryDecimal = true,
): string | number | null | undefined => {
  // 参数验证
  if (num === null || num === undefined || num === '') {
    return num;
  }

  try {
    // 转换为数字类型
    const numValue = typeof num === 'string' ? parseFloat(num) : num;

    // 检查是否为有效数字
    if (isNaN(numValue) || !isFinite(numValue)) {
      return num;
    }
    return new Intl.NumberFormat(locales, {
      minimumFractionDigits: isCompulsoryDecimal ? precision : 0,
      maximumFractionDigits: precision,
    }).format(numValue);
  } catch (error) {
    logger.warn('formatNumber error:', error);
    return num;
  }
};

// 是否展示会员专属标识
export function isShowMemberOnlyTag(params: any) {
  const { userInfo, landingPage = {}, productGroup = [] } = params;
  const flag = productGroup.find((item) => item.id === 390); // 当前商品属于分组390
  const { is_member } = userInfo || {};
  // 满足以下三个条件 是会员 && 黑五活动期间 && 商品属于分组390 展示会员专属商品标签
  return is_member === 1 && landingPage?.send_member_gift_icon === 1 && flag;
}

/**
 * @description: 把一个数字保留n位小数，并向下取余（默认2位）
 * @param {*} number 待处理数字
 * @param {*} length 保留小数位
 * @return {*} 处理后的字符串形式的数字
 */
export function floorNumber(number: number | string, length = 2) {
  const num = Number(number);
  const len = Number(length);
  // 不处理NaN和非大于0的情况; 不处理length异常
  if (!(num > 0) || !(len >= 0)) return '';
  const multiplier = Math.pow(10, len);
  return (Number(Math.floor(num * multiplier).toFixed()) / multiplier).toFixed(len);
}

// 获取设备类型
export function getDeviceTypeDetail(UserAgent) {
  let isChromeOnAndroid = false;
  if (/android/i.test(UserAgent)) {
    if (/chrome/i.test(UserAgent)) {
      const parts = UserAgent.split('chrome/');
      const fullVersionString = parts[1].split(' ')[0];
      const versionString = fullVersionString.split('.')[0];
      const version = parseInt(versionString, 10);
      if (version >= 27) {
        isChromeOnAndroid = true;
      }
    }
  }
  return {
    isMoble: /iphone|ipod|android.*mobile|windows.*phone|blackberry.*mobile/i.test(UserAgent), // 判断是否为移动端
    isIOS: /iphone|ipod|ipad|Macintosh/i.test(UserAgent), // 是否为苹果设备
    isMac: /Macintosh/i.test(UserAgent), // 是否为Mac笔记本
    isAndroid: /android/i.test(UserAgent), // 是否为安卓移动端
    isIphone: /iphone os/.test(UserAgent), // 是否为iphone
    isIpad: /ipad/.test(UserAgent), // 是否为ipad
    isChrome: /chrome/.test(UserAgent.substr(-33, 6)), // Chrome浏览器
    isSafari: /safari/.test(UserAgent) && !/chrome/.test(UserAgent), // safari浏览器
    isFirefox: /firefox/.test(UserAgent), // 火狐浏览器
    isOpera: /opera/.test(UserAgent), // Opera浏览器
    isUc: /ucweb/.test(UserAgent), // UC浏览器
    is360: /360se/.test(UserAgent), // 360浏览器
    isBaidu: /bidubrowser/.test(UserAgent), // 百度浏览器
    isSougou: /metasr/.test(UserAgent), // 搜狗浏览器
    isLB: /lbbrowser/.test(UserAgent), // 猎豹浏览器
    isWX: /micromessenger/.test(UserAgent), // 微信内置浏览器
    isQQ: /qqbrowser/.test(UserAgent), // QQ浏览器
    isIE6: /msie 6.0/.test(UserAgent), // IE6
    isIE7: /msie 7.0/.test(UserAgent), // IE7
    isIE8: /msie 8.0/.test(UserAgent), // IE8
    isIE9: /msie 9.0/.test(UserAgent), // IE9
    isIE10: /msie 10.0/.test(UserAgent), // IE10
    isIE11: /msie 11.0/.test(UserAgent), // IE11
    isWindowsCe: /windows ce/.test(UserAgent),
    isWindowsMobile: /windows mobile/.test(UserAgent),
    isWin2K: /windows nt 5.0/.test(UserAgent),
    isXP: /windows nt 5.1/.test(UserAgent),
    isVista: /windows nt 6.0/.test(UserAgent),
    isWin7: /windows nt 6.1/.test(UserAgent),
    isWin8: /windows nt 6.2/.test(UserAgent),
    isWin81: /windows nt 6.3/.test(UserAgent),
    isWin10: /windows nt 10.0/.test(UserAgent),
    isFaceBook: /fban/.test(UserAgent) || /fbav/.test(UserAgent),
    isInstagram: /instagram /.test(UserAgent),
    isTikTok: /bytedance/.test(UserAgent) || /musical_ly/.test(UserAgent),
    isChromeOnAndroid,
  };
}
// 获取设备版本号
export function getDeviceVersion(userAgent) {
  let version = '';
  // 判断是否为iOS设备
  if (getDeviceTypeDetail(userAgent).isIOS) {
    // 提取版本号信息
    const match = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
    // 组合版本号
    version = [parseInt(match[1], 10), parseInt(match[2], 10), parseInt(match[3] || '0', 10)].join(
      '.',
    );
  } else if (getDeviceTypeDetail(userAgent).isAndroid) {
    // 判断是否为Android设备
    // 提取版本号信息
    const match = userAgent.match(/Android\s([0-9.]*)/);
    // 获取版本号
    version = match[1];
  }
  return version;
}

// 根据cookie名称获取cookie值
export function getCookieValue(name: string) {
  const cookieList = document.cookie.split('; ');
  let cookieValue = '';
  cookieList.find((item) => {
    const itemInfor = item.split('=');
    if (itemInfor[0] === name) {
      cookieValue = itemInfor[1];
      return true;
    } else {
      return false;
    }
  });
  return cookieValue;
}

/* afterpay展示条件. afterpay支付对金额的要求是 >10     所选货币为（ AUD', 'CAD', 'NZD', 'USD  ）中的任意一种 */
export const afterpayEssentialMap = {
  AUD: 'AU',
  CAD: 'CA',
  USD: 'US',
  NZD: 'NZ',
};
/* klarna展示要求
    ■ 10≤商品金额≤1000（美元）
    ■ 10≤商品金额≤1500（欧元）
    ■ 30≤金额≤1000（英镑）
*/
export const klarnaEssentialMap = {
  USD: {
    CURRENCY: 'USD', // 币种
    MIN: 10, // 最小值
    MAX: 1000, // 最大值
    CYCLE: 4, // 分期期数
  },
  GBP: {
    CURRENCY: 'GBP',
    MIN: 30,
    MAX: 1000,
    CYCLE: 3,
  },
  EUR: {
    CURRENCY: 'EUR',
    MIN: 10,
    MAX: 1500,
    CYCLE: 3,
  },
};

export function numberUnitFormat(count) {
  // 类型判断和转换
  let num;
  if (typeof count === 'number') {
    num = count;
  } else if (typeof count === 'string') {
    num = parseFloat(count);
    if (isNaN(num)) {
      return 0;
    }
  } else {
    return 0;
  }

  // 格式化逻辑
  if (num < 0) return '0';
  if (num < 1000) {
    return num.toString();
  } else if (num < 1000000) {
    return `${Math.floor(num / 1000)}K`;
  } else if (num < 1000000000) {
    return `${Math.floor(num / 1000000)}M`;
  } else {
    return '999M';
  }
}

/* 对名字进行省略 */
export function nameOmission(str) {
  if (!str) return '';
  const arr = str.trim().split('');
  if (arr.length < 5) {
    return `${arr[0]}***`;
  } else {
    return `${arr[0]}***${arr[arr.length - 1]}`;
  }
}

/**
 * 计算显示的星级评分
 * @param starsValue 星级字符串值
 * @returns 计算后的星级数值，支持半星显示
 */
export function getDisplayStars(starsValue: string): number {
  const stars = parseFloat(starsValue);
  if (isNaN(stars) || stars < 0 || stars > 5) {
    return 5;
  }

  if (stars > 4 && stars < 5) {
    return 4.5;
  } else if (stars > 3 && stars < 4) {
    return 3.5;
  } else if (stars > 2 && stars < 3) {
    return 2.5;
  } else if (stars > 1 && stars < 2) {
    return 1.5;
  } else if (stars > 0 && stars < 1) {
    return 0.5;
  } else {
    return stars;
  }
}

/**
 * 生成随机ID的配置选项
 */
interface RandomIdOptions {
  /** ID长度，默认为8 */
  length?: number;
  /** 是否包含字母，默认为true */
  includeLetters?: boolean;
  /** 是否包含大写字母，默认为false */
  includeUppercase?: boolean;
  /** 是否包含特殊字符，默认为false */
  includeSpecialChars?: boolean;
  /** 自定义字符集 */
  customChars?: string;
}

/**
 * 生成指定长度和字符类型的随机ID
 * @param options - 配置选项
 * @returns 生成的随机ID字符串
 */
export function randomId(options: RandomIdOptions = {}): string {
  const {
    length = 8,
    includeLetters = true,
    includeUppercase = false,
    includeSpecialChars = false,
    customChars,
  } = options;

  // 如果提供了自定义字符集，直接使用
  if (customChars) {
    let result = '';
    const charactersLength = customChars.length;
    for (let i = 0; i < length; i++) {
      result += customChars.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  // 构建字符集
  const numbers = '0123456789';
  const lowercaseLetters = 'abcdefghijklmnopqrstuvwxyz';
  const uppercaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';

  let characters = numbers;

  if (includeLetters) {
    characters += lowercaseLetters;
  }

  if (includeUppercase) {
    characters += uppercaseLetters;
  }

  if (includeSpecialChars) {
    characters += specialChars;
  }

  let result = '';
  const charactersLength = characters.length;

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
}

export const checkEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const checkCode4Num = (code: string): boolean => {
  const codeRegex = /^\d{4}$/;
  return codeRegex.test(code);
};

export function addSearchParams(url: string, object: Record<string, any>) {
  const newUrl = new URL(url);
  for (const key in object) {
    if (!(!object[key] && object[key] !== 0 && object[key] !== false)) {
      newUrl.searchParams.set(key, encodeURIComponent(object[key]));
    }
  }
  return newUrl.toString();
}

/**
 * 设置本地存储
 * @param key 键
 * @param value 值
 */
export const setLocal = function (key: string, value: any) {
  localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
};

/**
 * 获取本地存储
 * @param key 键
 * @returns 解析后的值或null
 */
export const getLocal = function (key: string) {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return null;

    // 尝试解析JSON，如果失败则返回原字符串
    try {
      return JSON.parse(item);
    } catch {
      return item;
    }
  } catch {
    return null;
  }
};

/**
 * 支持过期时间的本地存储工具 (对应Vue项目的getSetLocalStorage)
 */
export const getSetLocalStorage = {
  /**
   * 设置带过期时间的本地存储
   * @param key 键
   * @param value 值
   * @param ttl_ms 过期时间戳（毫秒）
   */
  set(key: string, value: any, ttl_ms?: number) {
    try {
      const data = { value, expired: new Date(ttl_ms).getTime() };
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      logger.error('getSetLocalStorage.set error:', error);
    }
  },

  /**
   * 获取带过期检查的本地存储
   * @param key 键
   * @returns 有效值或null
   */
  get(key: string) {
    try {
      const item = localStorage.getItem(key);
      if (item === null) return null;

      const data: any = JSON.parse(item);
      if (data !== null) {
        if (data.expired != null && data.expired < new Date().getTime()) {
          // 过期了就删除
          localStorage.removeItem(key);
          return null;
        } else {
          return data.value;
        }
      }
      return null;
    } catch (error) {
      logger.error('getSetLocalStorage.get error:', error);
      return null;
    }
  },
};

/**
 * 设置本地存储
 * @param key 键
 * @param value 值
 */
export const setSession = function (key: string, value: any) {
  sessionStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
};

/**
 * 获取本地存储
 * @param key 键
 * @returns 解析后的值或null
 */
export const getSession = function (key: string) {
  try {
    const item = sessionStorage.getItem(key);
    if (item === null) return null;

    // 尝试解析JSON，如果失败则返回原字符串
    try {
      return JSON.parse(item);
    } catch {
      return item;
    }
  } catch {
    return null;
  }
};

/**
 * 支持过期时间的本地存储工具 (对应Vue项目的getSetSession)
 */
export const getSetSession = {
  /**
   * 设置带过期时间的本地存储
   * @param key 键
   * @param value 值
   * @param ttl_ms 过期时间戳（毫秒）
   */
  set(key: string, value: any, ttl_ms: number) {
    try {
      const data = { value, expired: new Date(ttl_ms).getTime() };
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      logger.error('getSetSession.set error:', error);
    }
  },

  /**
   * 获取带过期检查的本地存储
   * @param key 键
   * @returns 有效值或null
   */
  get(key: string) {
    try {
      const item = sessionStorage.getItem(key);
      if (item === null) return null;

      const data: any = JSON.parse(item);
      if (data !== null) {
        if (data.expired != null && data.expired < new Date().getTime()) {
          // 过期了就删除
          sessionStorage.removeItem(key);
          return null;
        } else {
          return data.value;
        }
      }
      return null;
    } catch (error) {
      logger.error('getSetSession.get error:', error);
      return null;
    }
  },
};

export const removeSession = function (key: string) {
  sessionStorage.removeItem(key);
};

// 生成游客标识
export function visitor() {
  let guid = '';
  for (let i = 1; i <= 16; i++) {
    const flag = Math.floor(Math.random() * 10);
    // 如果是偶数就设置成为
    if (flag % 2 === 0) {
      // 全大写
      const n = Math.floor(flag * 2.4) + 65;
      // 转换为字符
      const char = String.fromCharCode(n);
      guid += char;
    } else {
      guid += flag;
    }
  }
  return guid;
}

// 能够匹配 /good-list/id 这种路径，但是不能够匹配 /paymentSuccess 这种路径
export const checkHeaderConfig = (pathname: string, isMobile: boolean) => {
  const headerConfig = {
    isShowHeaderNav: true,
    isHeaderDarkMode: false,
    isShowDtBanner: true,
    isShowAppDownloadBanner: false,
  };
  if (
    HIDE_HEADER_PAGE_LIST[isMobile ? 'WAP' : 'WEB'].some((path) => {
      if (path === '/') {
        return pathname === '/';
      } else {
        return pathname === path || pathname?.startsWith(`${path}/`);
      }
    })
  ) {
    headerConfig.isShowHeaderNav = false;
  }
  if (
    HEADER_DARK_MODE_PAGE_LIST.some((path) => {
      if (path === '/') {
        return pathname === '/';
      } else {
        return pathname === path || pathname?.startsWith(`${path}/`);
      }
    })
  ) {
    headerConfig.isHeaderDarkMode = true;
  }
  if (
    HIDE_DT_BANNER_PAGE_LIST.some((path) => {
      if (path === '/') {
        return pathname === '/';
      } else {
        return pathname === path || pathname?.startsWith(`${path}/`);
      }
    })
  ) {
    headerConfig.isShowDtBanner = false;
  }

  if (
    SHOW_APP_DOWNLOAD_BANNER_PAGE_LIST[isMobile ? 'WAP' : 'WEB'].some((item) => {
      if (item === '/') {
        return pathname === '/';
      } else {
        return pathname === item || pathname?.startsWith(`${item}/`);
      }
    })
  ) {
    headerConfig.isShowAppDownloadBanner = true;
  }
  return headerConfig;
};

export const checkFooterConfig = (pathname: string, isMobile: boolean) => {
  const footerConfig = {
    isShowFooterNav: true,
    isShowFooterSubscribe: false,
  };

  // 获取对应平台的隐藏页面列表
  const hideFooterPageList = HIDE_FOOTER_PAGE_LIST[isMobile ? 'WAP' : 'WEB'];

  // 在循环中判断：如果是首页路径用全等于，其他路径用startsWith
  if (
    hideFooterPageList.some((path) => {
      if (path === '/') {
        return pathname === '/';
      } else {
        return pathname === path || pathname?.startsWith(`${path}/`);
      }
    })
  ) {
    footerConfig.isShowFooterNav = false;
  }

  // 检查是否需要显示订阅框
  if (
    SHOW_FOOTER_SUBSCRIBE_PAGE_LIST[isMobile ? 'WAP' : 'WEB'].some((item) => {
      if (item === '/') {
        return pathname === '/';
      } else {
        return pathname === item || pathname?.startsWith(`${item}/`);
      }
    })
  ) {
    footerConfig.isShowFooterSubscribe = true;
  }

  return footerConfig;
};

/**
 * 滚动到指定锚点，支持偏移量
 * @param anchorId 锚点元素的ID
 * @param offset 向上偏移的像素数，默认100px
 * @param behavior 滚动行为，默认为 'smooth'
 */
export const scrollToAnchor = (
  anchorId: string,
  offset = 100,
  behavior: ScrollBehavior = 'smooth',
  container: Document | ShadowRoot | HTMLElement = document,
) => {
  const element = container.querySelector(anchorId);
  if (element) {
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior,
    });
  }
};
// 加购成功之后，本地缓存购物车id，便于在购物车页面加载时将这里缓存的id默认勾选
export function setJustAddCart(ids = []) {
  try {
    getSetLocalStorage.set('zl-justAddToCart', ids);
  } catch (error) {
    logger.error('setJustAddCart error:', error);
  }
}

// 处理换行的辅助函数
export const processLineBreaks = (text: string): string => {
  return text?.replace(/\n/g, '<br/>') || '';
};

// 把数组内的每一项字符串的首字母改为大写
export function capitalizeArr(arr) {
  try {
    if (!Array.isArray(arr)) {
      return [];
    }
    const newArr = [];
    arr.forEach((item) => {
      newArr.push(capitalize(item));
    });
    return newArr;
  } catch (error) {
    logger.error('capitalizeArr error:', error);
    return arr;
  }
}

// 首字母大写
export function capitalize(str) {
  if (typeof str !== 'string' || str.length === 0) {
    return str;
  }
  try {
    // 查找第一个字母字符的位置
    const firstLetterIndex = str.search(/[a-zA-Z]/);
    if (firstLetterIndex === -1) {
      // 如果字符串中没有字母，则返回原字符串
      return str;
    }

    // 转换第一个字母为大写，其余字母转换为小写
    return (
      str.substring(0, firstLetterIndex) +
      str.charAt(firstLetterIndex).toUpperCase() +
      str.substring(firstLetterIndex + 1).toLowerCase()
    );
  } catch (error) {
    logger.error('capitalize error:', error);
    return str;
  }
}

/**
 * 获取首页模块的图片alt属性
 * 首页及其它的alt规则：
 * - 有配置图片名称的：取图片名称+glasses
 * - 有版位标题的：取版位标题+glasses+序号
 * - 无版位标题的：Zeelool optical eyeglasses online store+ 组件名称+序号
 *
 * @param componentName 组件名称
 * @param index 图片索引
 * @param indexData 首页数据
 * @param title 图片标题
 * @returns alt文本
 */
export function getAlt(
  componentName: string,
  index: number,
  indexData: any,
  title?: string,
): string {
  let text = '';

  if (title) {
    text = `${title} glasses`;
  } else if (indexData?.layout_title) {
    text = `${indexData.layout_title} glasses ${index || 0}`;
  } else {
    text = `Zeelool optical eyeglasses online store ${componentName}${index || 0}`;
  }
  try {
    // 移除HTML标签
    return text.replace(/<[^>]*>/g, '');
  } catch (error) {
    logger.error('getAlt error:', error);
    return text;
  }
}

/**
 * 检查路径是否为视频文件
 * @param path 文件路径
 * @returns 是否为视频文件
 */
export function checkPathForMp4(path?: string): boolean {
  if (!path) return false;
  return path.toLowerCase().includes('.mp4') || path.toLowerCase().includes('.webm');
}
// 为了兼容主站和小语种站，根据站点获取不同的locales
export function getIntlLocale(site: typeof env.NEXT_PUBLIC_SITE_NAME) {
  // 只有5个站，us cauk defr, 德法默认都用de-DE，其他都用en-US
  const isDeFr = ['de', 'fr'].includes(site);
  if (isDeFr) {
    return 'de-DE';
  }
  return 'en-US';
}

// export function isLighthouseTest(deviceType: EPlatform) {
//   return (
//     (deviceType === EPlatform.WEB && navigator.plugins.length === 0) ||
//     (deviceType !== EPlatform.WEB &&
//       navigator.userAgent.includes('moto g') &&
//       navigator.userAgent.toLowerCase().includes('android 11'))
//   );
// }

/** 与 getServerTokenFromCookie 一致：app_token 优先，其次 token */
export function readAuthTokenFromCookie(): string {
  return Cookie.get('app_token') || Cookie.get('token') || '';
}
